import { useEffect, useState, useCallback } from 'react';
import { Copy, Check, AlertTriangle, Gift, UserPlus } from 'lucide-react';
import { api } from '../../../api/client';

type Invite = {
  id: string;
  referredTenantId: string;
  status: 'signed_up' | 'first_paid' | 'rewarded' | 'expired' | 'revoked';
  codeUsed: string;
  signedUpAt: string;
  firstPaidAt: string | null;
  rewardedAt: string | null;
  creditMonthsAwarded: number | null;
};

type Credit = {
  id: string;
  sourceType: string;
  sourceReferralId: string | null;
  monthsTotal: number;
  monthsUsed: number;
  note: string | null;
  expiresAt: string | null;
  createdAt: string;
};

type Status = {
  code: string;
  invites: Invite[];
  credits: Credit[];
  availableMonths: number;
};

const statusColors: Record<Invite['status'], { bg: string; color: string; label: string }> = {
  signed_up: { bg: 'rgba(59,130,246,0.2)', color: '#60a5fa', label: 'Čeka prvu uplatu' },
  first_paid: { bg: 'rgba(234,179,8,0.2)', color: '#facc15', label: 'Uplaćeno' },
  rewarded: { bg: 'rgba(34,197,94,0.2)', color: '#4ade80', label: 'Nagrađen' },
  expired: { bg: 'rgba(156,163,175,0.2)', color: '#9ca3af', label: 'Isteklo' },
  revoked: { bg: 'rgba(239,68,68,0.2)', color: '#fca5a5', label: 'Povučeno' },
};

export function ReferralTab({ tenantId }: { tenantId: string }) {
  const [status, setStatus] = useState<Status | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const fetchStatus = useCallback(async () => {
    setError(null);
    try {
      const data = await api.get<Status>(`/admin/referral/status/${tenantId}`);
      setStatus(data);
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška pri učitavanju');
    }
  }, [tenantId]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const shareLink = status ? `${window.location.origin}/register?ref=${status.code}` : '';

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // noop
    }
  };

  if (error) return <div style={errorBox}><AlertTriangle size={14} /> {error}</div>;
  if (!status) return <div style={{ color: 'var(--color-text-muted)' }}>Učitavanje...</div>;

  const rewarded = status.invites.filter((i) => i.status === 'rewarded').length;
  const pending = status.invites.filter((i) => i.status === 'signed_up' || i.status === 'first_paid').length;

  return (
    <div>
      {/* Top summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 20 }}>
        <Stat label="Dostupno kredita" value={`${status.availableMonths} ${status.availableMonths === 1 ? 'mesec' : 'meseci'}`} icon={<Gift size={16} />} />
        <Stat label="Uspešno pozvano" value={String(rewarded)} icon={<Check size={16} />} />
        <Stat label="Čeka uplatu" value={String(pending)} icon={<UserPlus size={16} />} />
      </div>

      {/* Code + share */}
      <Section title="Referal kod">
        <div style={{ marginBottom: 10, fontSize: 12, color: 'var(--color-text-muted)' }}>
          Deli ovaj kod sa kolegama. Kada se novi klijent registruje sa kodom i plati prvu fakturu,
          oboje dobijate 1 mesec besplatno.
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
          <input type="text" value={status.code} readOnly style={{ ...inputStyle, fontFamily: 'Menlo, Consolas, monospace', fontWeight: 700, letterSpacing: 1 }} />
          <button onClick={() => handleCopy(status.code)} style={btnSecondary} title="Kopiraj kod">
            {copied ? <Check size={14} /> : <Copy size={14} />} Kopiraj
          </button>
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <input type="text" value={shareLink} readOnly style={inputStyle} />
          <button onClick={() => handleCopy(shareLink)} style={btnSecondary} title="Kopiraj link">
            {copied ? <Check size={14} /> : <Copy size={14} />} Link
          </button>
        </div>
      </Section>

      {/* Invites table */}
      <Section title={`Pozvani klijenti (${status.invites.length})`}>
        {status.invites.length === 0 ? (
          <div style={{ padding: 20, textAlign: 'center', color: 'var(--color-text-muted)', fontSize: 13 }}>
            Još uvek nema pozvanih klijenata.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={th}>Kod</th>
                  <th style={th}>Status</th>
                  <th style={th}>Prijavljen</th>
                  <th style={th}>Platio</th>
                  <th style={th}>Nagrađen</th>
                  <th style={{ ...th, textAlign: 'right' }}>Krediti</th>
                </tr>
              </thead>
              <tbody>
                {status.invites.map((i) => {
                  const sc = statusColors[i.status];
                  return (
                    <tr key={i.id}>
                      <td style={{ ...td, fontFamily: 'Menlo, Consolas, monospace', fontSize: 12 }}>{i.codeUsed}</td>
                      <td style={td}>
                        <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 8, background: sc.bg, color: sc.color, fontWeight: 700 }}>
                          {sc.label}
                        </span>
                      </td>
                      <td style={{ ...td, fontVariantNumeric: 'tabular-nums' }}>{fmtDate(i.signedUpAt)}</td>
                      <td style={{ ...td, fontVariantNumeric: 'tabular-nums' }}>{fmtDate(i.firstPaidAt)}</td>
                      <td style={{ ...td, fontVariantNumeric: 'tabular-nums' }}>{fmtDate(i.rewardedAt)}</td>
                      <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                        {i.creditMonthsAwarded || 0}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* Credits breakdown */}
      {status.credits.length > 0 && (
        <Section title="Istorija kredita">
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={th}>Izvor</th>
                  <th style={th}>Iskorišćeno / Ukupno</th>
                  <th style={th}>Datum</th>
                  <th style={th}>Napomena</th>
                </tr>
              </thead>
              <tbody>
                {status.credits.map((c) => (
                  <tr key={c.id}>
                    <td style={td}>{c.sourceType}</td>
                    <td style={{ ...td, fontVariantNumeric: 'tabular-nums' }}>{c.monthsUsed} / {c.monthsTotal}</td>
                    <td style={{ ...td, fontVariantNumeric: 'tabular-nums' }}>{fmtDate(c.createdAt)}</td>
                    <td style={{ ...td, color: 'var(--color-text-muted)' }}>{c.note || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}
    </div>
  );
}

function Stat({ label, value, icon }: { label: string; value: string; icon: React.ReactNode }) {
  return (
    <div style={{ background: 'var(--color-bg-surface)', borderRadius: 10, border: '1px solid var(--color-border)', padding: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
        {icon} {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--color-text-strong)' }}>{value}</div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ background: 'var(--color-bg-surface)', borderRadius: 10, border: '1px solid var(--color-border)', padding: 16, marginBottom: 16 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 12 }}>{title}</div>
      {children}
    </div>
  );
}

const fmtDate = (d: string | null) => d ? new Date(d).toLocaleDateString('sr-Latn-RS') : '-';

const th: React.CSSProperties = { padding: '8px 10px', fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'left', borderBottom: '1px solid var(--color-border)' };
const td: React.CSSProperties = { padding: '10px', fontSize: 13, color: 'var(--color-text)', borderBottom: '1px solid var(--color-border)' };
const inputStyle: React.CSSProperties = { padding: '8px 12px', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 13, background: 'var(--color-bg)', color: 'var(--color-text)', flex: 1 };
const btnSecondary: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 6, border: '1px solid var(--color-border)', background: 'var(--color-bg-surface)', color: 'var(--color-text)', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const errorBox: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 6, background: 'rgba(239,68,68,0.15)', color: '#fca5a5', fontSize: 13, marginBottom: 16 };
